import React, { Children } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function PrivacyPolicy() {
  useDocumentMeta({
    title: 'Privacy Policy | VCA Capital Advisors',
    description: 'VCA Capital Advisors privacy policy. How we collect, use, and protect the personal and business information of our clients.',
    path: '/privacy'
  });
  return <div className="min-h-screen bg-black text-white font-sans selection:bg-white/20" data-id="element-603">
      <Navigation data-id="element-604" />

      <main className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-605">
        <div className="mb-12" data-id="element-606">
          <h1 className="text-4xl md:text-5xl font-medium mb-4 tracking-tight" data-id="element-607">
            Privacy Policy
          </h1>
          <p className="text-zinc-400" data-id="element-608">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </div>

        <div className="space-y-10 text-zinc-300 leading-relaxed" data-id="element-609">
          <section data-id="element-610">
            <p data-id="element-611">
              VCA Capital Advisors Inc. ("we," "our," or "us") is committed to
              protecting your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you
              visit our website or use our business funding brokerage and
              advisory services.
            </p>
          </section>

          <section data-id="element-612">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-613">
              1. Information We Collect
            </h2>
            <p className="mb-3" data-id="element-614">
              We may collect personal and business information that you
              voluntarily provide to us, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-615">
              <li data-id="element-616">
                Contact information (name, email address, phone number, mailing
                address)
              </li>
              <li data-id="element-617">
                Business information (company name, industry, time in business,
                revenue)
              </li>
              <li data-id="element-618">Financial information necessary for funding applications</li>
              <li data-id="element-619">
                Information provided through forms, applications, or
                communications with us
              </li>
            </ul>
          </section>

          <section data-id="element-620">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-621">
              2. How We Use Your Information
            </h2>
            <p className="mb-3" data-id="element-622">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-623">
              <li data-id="element-624">Provide, operate, and maintain our services</li>
              <li data-id="element-625">Process and evaluate your funding applications</li>
              <li data-id="element-626">
                Communicate with you regarding your application, account, or our
                services
              </li>
              <li data-id="element-627">
                Send you administrative information, such as updates to our
                terms or policies
              </li>
              <li data-id="element-628">Respond to your inquiries and provide customer support</li>
              <li data-id="element-629">Improve our website and services</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 p-6 rounded-xl border border-zinc-800" data-id="element-630">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-631">
              3. SMS/Text Messaging Consent and Policy
            </h2>
            <p className="mb-3" data-id="element-632">
              VCA Capital Advisors Inc. sends SMS messages to customers and
              registered portal users who create an account at
              vcacapitaladvisors.com/portal. Messages are strictly transactional
              and account-related. We do not send marketing or promotional SMS.
            </p>
            <p className="mb-2 font-medium text-white" data-id="element-633">Message types include:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-634">
              <li data-id="element-635">
                Two-factor authentication (2FA) and one-time passcodes (OTP)
              </li>
              <li data-id="element-636">Sign-in alerts and account access notifications</li>
              <li data-id="element-637">Password reset confirmations</li>
              <li data-id="element-638">Account and document status notifications</li>
              <li data-id="element-639">Document request notifications</li>
              <li data-id="element-640">Account security and account-change alerts</li>
            </ul>
            <ul className="list-disc pl-6 space-y-2" data-id="element-641">
              <li data-id="element-642">
                <strong data-id="element-643">Message Frequency:</strong> Variable, based on your
                account activity.
              </li>
              <li data-id="element-644">
                <strong data-id="element-645">Rates:</strong> Standard message and data rates may
                apply depending on your carrier plan.
              </li>
              <li data-id="element-646">
                <strong data-id="element-647">Opt-Out:</strong> Reply <strong data-id="element-648">STOP</strong> to any
                message at any time. You will receive one final confirmation
                message and no further texts.
              </li>
              <li data-id="element-649">
                <strong data-id="element-650">Help:</strong> Reply <strong data-id="element-651">HELP</strong> for
                assistance, or contact us at info@vcacapitaladvisors.com.
              </li>
              <li data-id="element-652">
                <strong data-id="element-653">Supported Carriers:</strong> AT&amp;T, T-Mobile,
                Verizon, Sprint, Boost Mobile, Cricket Wireless, MetroPCS, U.S.
                Cellular, Virgin Mobile, and others. Carriers are not liable for
                delayed or undelivered messages.
              </li>
              <li data-id="element-654">
                <strong data-id="element-655">No Marketing Sharing:</strong> No mobile information
                will be shared with third parties or affiliates for marketing or
                promotional purposes. All categories listed in this Privacy
                Policy exclude text messaging originator opt-in data and consent;
                this information will not be shared with any third parties.
              </li>
              <li data-id="element-656">
                <strong data-id="element-657">Not a Condition:</strong> SMS consent is not a condition
                of purchase, application, or receiving services from VCA Capital
                Advisors Inc.
              </li>
            </ul>
          </section>

          <section data-id="element-658">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-659">
              4. Data Sharing and Disclosure
            </h2>
            <p className="mb-3" data-id="element-660">
              We may share your information in the following situations:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-661">
              <li data-id="element-662">
                <strong data-id="element-663">With Funding Partners:</strong> We share necessary
                business and financial information with our network of lenders
                and funding partners to secure capital for your business.
              </li>
              <li data-id="element-664">
                <strong data-id="element-665">Service Providers:</strong> We may share information
                with third-party vendors who perform services on our behalf
                (e.g., hosting, data analysis, customer service).
              </li>
              <li data-id="element-666">
                <strong data-id="element-667">Legal Requirements:</strong> We may disclose your
                information if required by law, court order, or governmental
                regulation.
              </li>
            </ul>
            <p className="mt-3 font-medium text-white" data-id="element-668">
              Note: No mobile information will be shared with third parties or
              affiliates for marketing or promotional purposes. All categories
              listed in this Privacy Policy exclude text messaging originator
              opt-in data and consent; this information will not be shared with
              any third parties.
            </p>
          </section>

          <section data-id="element-669">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-670">
              5. Data Security
            </h2>
            <p data-id="element-671">
              We implement reasonable administrative, technical, and physical
              security measures to protect your personal information. However,
              no electronic transmission over the internet or information
              storage technology can be guaranteed to be 100% secure, so we
              cannot promise or guarantee that hackers, cybercriminals, or other
              unauthorized third parties will not be able to defeat our security
              and improperly collect, access, steal, or modify your information.
            </p>
          </section>

          <section data-id="element-672">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-673">
              6. Cookies and Tracking
            </h2>
            <p data-id="element-674">
              We may use cookies, web beacons, tracking pixels, and other
              tracking technologies on our website to help customize the site
              and improve your experience. You can choose to disable cookies
              through your browser settings, but doing so may affect your
              ability to use certain features of our website.
            </p>
          </section>

          <section data-id="element-675">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-676">
              7. Your Rights
            </h2>
            <p data-id="element-677">
              Depending on your location (such as under the CCPA or TCPA), you
              may have certain rights regarding your personal information,
              including the right to access, correct, or delete your data. To
              exercise these rights, please contact us using the information
              provided below.
            </p>
          </section>

          <section data-id="element-678">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-679">
              8. Children's Privacy
            </h2>
            <p data-id="element-680">
              Our services are intended for businesses and business owners. We
              do not knowingly solicit information from or market to children
              under the age of 18. If we learn that we have collected personal
              information from a child under age 18, we will delete that
              information as quickly as possible.
            </p>
          </section>

          <section data-id="element-681">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-682">
              9. Changes to This Policy
            </h2>
            <p data-id="element-683">
              We may update this Privacy Policy from time to time. The updated
              version will be indicated by an updated "Last Updated" date and
              the updated version will be effective as soon as it is accessible.
              We encourage you to review this Privacy Policy frequently to be
              informed of how we are protecting your information.
            </p>
          </section>

          <section data-id="element-684">
            <h2 className="text-2xl font-medium text-white mb-4" data-id="element-685">
              10. Contact Us
            </h2>
            <p className="mb-4" data-id="element-686">
              If you have questions or comments about this Privacy Policy,
              please contact us at:
            </p>
            <div className="bg-zinc-900/30 p-6 rounded-xl border border-zinc-800" data-id="element-687">
              <p className="font-medium text-white mb-1" data-id="element-688">
                VCA Capital Advisors Inc.
              </p>
              <p data-id="element-689">2248 Broadway STE 1784</p>
              <p data-id="element-690">New York, NY 10024</p>
              <p className="mt-2" data-id="element-691">
                Email:{' '}
                <a href="mailto:info@vcacapitaladvisors.com" className="text-white hover:underline" data-id="element-692">
                  info@vcacapitaladvisors.com
                </a>
              </p>
              <p data-id="element-693">
                Phone:{' '}
                <a href="tel:646-331-2752" className="text-white hover:underline" data-id="element-694">
                  (646) 331-2752
                </a>
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer data-id="element-695" />
    </div>;
}